/**
 * UserAvatar.jsx — Reusable user identity avatar
 *
 * Renders a colored circle with initials (or an image if avatarUrl provided).
 * For prospects without avatarUrl, tries /api/agent-photos/{First_Last}.jpg.
 * Uses the user's accent_color for consistent identity across the platform.
 *
 * Sizes: xs (16), sm (20), md (24), lg (40), xl (64)
 */
import { useState } from 'react';
import { getUserColor, getInitials, agentPhotoUrl } from '../../lib/userColors';

const SIZES = {
  xs: { px: 16, font: '0.5rem' },
  sm: { px: 20, font: '0.55rem' },
  md: { px: 24, font: '0.6rem' },
  lg: { px: 40, font: '0.85rem' },
  xl: { px: 64, font: '1.25rem' },
};

export default function UserAvatar({ name, color, avatarUrl, contactType, size = 'md', className = '', style = {}, onClick }) {
  const [imgError, setImgError] = useState(false);
  const s = SIZES[size] || SIZES.md;
  const bg = color || getUserColor({ name });
  const initials = getInitials(name);

  // Determine image source: explicit avatarUrl, or try agent-photos for prospects
  let src = avatarUrl || null;
  if (!src && contactType === 'prospect' && name) {
    src = agentPhotoUrl(name);
  }

  return (
    <div
      className={`inline-flex items-center justify-center rounded-full flex-shrink-0 font-bold ${className}`}
      style={{
        width: s.px,
        height: s.px,
        fontSize: s.font,
        backgroundColor: (src && !imgError) ? 'transparent' : bg,
        color: '#fff',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
      title={name}
      onClick={onClick}
    >
      {src && !imgError ? (
        <img src={src} alt={name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
      ) : (
        initials
      )}
    </div>
  );
}
